<script lang="ts">
import type { DrawerSlots } from '@nuxt/ui';
import type { ButtonProps, DrawerProps } from '#layers/ui';
import { mergeUi } from '#layers/ui';

export interface DrawerPageProps {
	title?: string;
	description?: string;
	headerActions?: ButtonProps[];
	footerActions?: ButtonProps[];
	/**
	 * Attach this drawer to the route.
	 *
	 * If it is attached to the route, closing the drawer will pop the current route at the end of the leave animation
	 *
	 * @default true
	 */
	attachToRoute?: boolean;
	/**
	 * Show a default close action next to the drawer title.
	 *
	 * @default true
	 */
	showCloseAction?: boolean;
	ui?: DrawerProps['ui'];
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { ref, toRefs, watch } from 'vue';
import { useHead } from '#app/composables/head';
import { useRoute, useRouter } from '#app/composables/router';
import { useAppConfig } from '#app/config';
import { useProvideDrawerSize } from '#layers/base';
import { omit } from '#layers/base/utils/object';

const props = withDefaults(defineProps<DrawerPageProps>(), {
	attachToRoute: true,
	showCloseAction: true,
});

const emit = defineEmits<{
	close: [];
}>();

const slots = defineSlots<DrawerSlots & {
	'header-actions': () => void;
	'footer-actions': () => void;
}>();

const { containerClass } = useProvideDrawerSize();

const { title } = toRefs(props);
const forward = reactivePick(props, 'title', 'description');
const proxySlots = omit(slots, ['default', 'body', 'header', 'header-actions', 'footer-actions']);

const appConfig = useAppConfig();
const router = useRouter();
const route = useRoute();

const open = ref(true);

watch(open, () => {
	if (!open.value) {
		emit('close');
	}
});

function close() {
	open.value = false;
}

function onAnimationEnd(open: boolean) {
	if (!open && props.attachToRoute) {
		const parentRoute = route.matched.at(-2);

		if (!parentRoute) {
			return;
		}

		router.replace({
			name: parentRoute.name,
		});
	}
}

if (props.attachToRoute) {
	useHead({
		title,
	});
}

defineExpose({
	close,
});
</script>

<template>
	<!-- Don't set `:modal` prop on `DDrawer` otherwise it fully unmounts and re-mounts the content, see https://github.com/unovue/reka-ui/blob/a202067f7605cb33eb5fa5bdd7a7fafeeedce565/packages/core/src/Dialog/DialogContent.vue#L36-L49  -->

	<DDrawer
		v-model:open="open"
		v-bind="forward"
		direction="right"
		:handle="false"
		:ui="mergeUi({
			content: 'max-w-[calc(100%-var(--spacebar-width))]',
			container: `@container ${containerClass} gap-0 transition-[width]`,
			body: 'overflow-y-auto py-8',
		} as DrawerProps['ui'], props.ui)"
		@animation-end="onAnimationEnd"
	>
		<template v-for="(_, name) in proxySlots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>

		<template #header>
			<slot name="header">
				<div class="flex w-full flex-1 flex-col">
					<h2 class="flex-1 text-sm font-semibold">
						<slot name="title">
							{{ title }}
						</slot>
					</h2>
					<p v-if="!!$slots.description || description" class="-mt-1 text-sm text-dimmed">
						<slot name="description">
							{{ description }}
						</slot>
					</p>
				</div>

				<slot name="header-actions">
					<div class="flex items-center gap-4">
						<DButton v-for="(action, index) in headerActions" :key="index" v-bind="action" />

						<DButton
							v-if="showCloseAction"
							:icon="appConfig.ui.icons.close"
							color="neutral"
							variant="ghost"
							:aria-label="$t('common.close')"
							@click="close()"
						/>
					</div>
				</slot>
			</slot>
		</template>

		<template #body>
			<Suspense>
				<slot />
			</Suspense>
		</template>

		<template v-if="footerActions || !!slots['footer-actions']" #footer>
			<div class="flex items-center justify-end gap-4 inline-full">
				<slot name="footer-actions">
					<DButton v-for="(action, index) in footerActions" :key="index" v-bind="action" />
				</slot>
			</div>
		</template>
	</DDrawer>
</template>
